import { useTranslation } from '@/hooks/useTranslation';
import { useContext, useMemo } from 'react';
import {
  AlertCircle,
  Clock,
  Users,
  Coins,
  Settings,
  Activity,
  User,
  LogOut,
  Volleyball,
  Film,
  Clapperboard,
  Tags,
  Antenna,
  Tv,
  MicVocal,
  Shield,
  ArrowLeftRight,
  Radio,
  Globe,
  Megaphone,
  FileText,
  Calculator,
  BarChart3,
  AppWindow,
} from 'lucide-react';
import Dropdown from '../ui/Dropdown.tsx';
import MobileMenu from '../ui/MobileMenu.tsx';
import { DropdownItem } from '@/types/components';
import { AuthContext } from '@/context/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';
import { PermissionGate } from '../ui/PermissionGate.tsx';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useBranding } from '@/context/BrandingContext';
import ThemeToggle from '@/components/ui/ThemeToggle';

const NavigationBar = () => {
  const { t } = useTranslation();
  const { user, logout } = useContext(AuthContext);
  const { hasAnyPermission } = usePermissions();
  const queryClient = useQueryClient();
  const { branding } = useBranding();
  const navLogo = branding.logoCompactUrl || branding.logoUrl;
  const modules = branding.modules ?? {};
  const isModuleEnabled = (enabled?: boolean) => enabled === true;
  const tvChannelsEnabled = isModuleEnabled(modules.tvChannels);
  const liveTvCountriesEnabled = isModuleEnabled(modules.liveTvCountriesEnabled);
  const reportsEnabled = isModuleEnabled(modules.reports);
  const accountingEnabled = isModuleEnabled(modules.accounting);
  const adsEnabled = isModuleEnabled(modules.ads);
  const showLiveTvCountries = tvChannelsEnabled && liveTvCountriesEnabled;

  const handleLogout = async () => {
    try {
      await logout();
      queryClient.removeQueries()
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Filter admin items based on permissions
  const adminItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = [];

    if (hasAnyPermission(['USER_READ', 'USER_CREATE', 'USER_UPDATE', 'USER_DELETE'])) {
      items.push({ label: 'Usuarios', icon: Users, route: '/users' });
    }

    if (hasAnyPermission(['VIEW_HISTORY_READ'])) {
      items.push({ label: 'Historial de visitas', icon: Clock, route: '/visit-history' });
    }

    if (hasAnyPermission(['ISSUES_READ'])) {
      items.push({ label: 'Errores Reproducción', icon: AlertCircle, route: '/issues' });
    }

    if (hasAnyPermission(['CREDIT_HISTORY_READ'])) {
      items.push({ label: 'Movimiento Crédito', icon: OverlappingIcons, route: '/credits-history' });
    }

    if (accountingEnabled && hasAnyPermission(['CREDIT_HISTORY_READ'])) {
      items.push({ label: 'Contabilidad', icon: Calculator, route: '/accounting' });
    }

    return items;
  }, [accountingEnabled, hasAnyPermission]);

  // Filter content items based on permissions
  const contentItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = [];

    if (isModuleEnabled(modules.categories) && hasAnyPermission(['CATEGORY_READ', 'CATEGORY_CREATE', 'CATEGORY_UPDATE', 'CATEGORY_DELETE'])) {
      items.push({ label: 'Categorías', icon: Tags, route: '/content/categories' });
    }

    if (tvChannelsEnabled && hasAnyPermission(['TVCHANNEL_READ', 'TVCHANNEL_CREATE', 'TVCHANNEL_UPDATE', 'TVCHANNEL_DELETE'])) {
      items.push({ label: 'Canales de TV', icon: Antenna, route: '/content/tv-channels' });
      if (showLiveTvCountries) {
        items.push({ label: 'Países', icon: Globe, route: '/content/countries' });
      }
    }

    if (isModuleEnabled(modules.radioStations) && hasAnyPermission(['RADIOSTATION_READ', 'RADIOSTATION_CREATE', 'RADIOSTATION_UPDATE', 'RADIOSTATION_DELETE'])) {
      items.push({ label: 'Estaciones de Radio', icon: Radio, route: '/content/radio-stations' });
    }

    if (isModuleEnabled(modules.documentaries) && hasAnyPermission(['DOCUMENTARY_READ', 'DOCUMENTARY_CREATE', 'DOCUMENTARY_UPDATE', 'DOCUMENTARY_DELETE'])) {
      items.push({ label: 'Documentales', icon: Clapperboard, route: '/content/documentaries' });
    }

    if (isModuleEnabled(modules.movies) && hasAnyPermission(['MOVIE_READ', 'MOVIE_CREATE', 'MOVIE_UPDATE', 'MOVIE_DELETE'])) {
      items.push({ label: 'Películas', icon: Film, route: '/content/movies' });
    }

    if (isModuleEnabled(modules.concerts) && hasAnyPermission(['CONCERT_READ', 'CONCERT_CREATE', 'CONCERT_UPDATE', 'CONCERT_DELETE'])) {
      items.push({ label: 'Conciertos', icon: MicVocal, route: '/content/concerts' });
    }

    if (isModuleEnabled(modules.tvShows) && hasAnyPermission(['TVSHOW_READ', 'TVSHOW_CREATE', 'TVSHOW_UPDATE', 'TVSHOW_DELETE'])) {
      items.push({ label: 'Series', icon: Tv, route: '/content/tv-shows' });
    }

    if (isModuleEnabled(modules.sports) && hasAnyPermission(['SPORT_READ', 'SPORT_CREATE', 'SPORT_UPDATE', 'SPORT_DELETE'])) {
      items.push({ label: 'Deportes', icon: Volleyball, route: '/content/sports' });
    }

    return items;
  }, [hasAnyPermission, showLiveTvCountries, tvChannelsEnabled, modules.categories, modules.radioStations, modules.documentaries, modules.movies, modules.concerts, modules.tvShows, modules.sports]);

  const settingsItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = [
      { label: 'General', icon: Settings, route: '/settings/general' }
    ];

    if (user?.roleCode === 'superadmin') {
      items.push({ label: 'Application', icon: AppWindow, route: '/settings/application' });
      items.push({ label: 'Métricas', icon: Activity, route: '/settings/metrics' });
    }

    if (modules.audienceManagementEnabled && ['admin', 'superadmin'].includes(user?.roleCode ?? '')) {
      items.push({ label: t.audience.groups, icon: Users, route: '/settings/audiences' });
    }

    return items;
  }, [user?.roleCode, modules.audienceManagementEnabled, t.audience.groups]);

  const reportItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = [];

    if (reportsEnabled && hasAnyPermission(['CREDIT_HISTORY_READ'])) {
      items.push({ label: 'Ventas de créditos', icon: FileText, route: '/reports/credit-sales' });
    }
    if (reportsEnabled && (user?.roleCode === 'admin' || user?.roleCode === 'superadmin' || user?.roleCode === 'contentManager')) {
      items.push({ label: 'Productividad de contenido', icon: BarChart3, route: '/reports/content-productivity' });
    }

    return items;
  }, [hasAnyPermission, reportsEnabled, user?.roleCode]);

  const adsItems: DropdownItem[] = useMemo(() => {
    const items: DropdownItem[] = [];

    if (adsEnabled && hasAnyPermission(['AD_CAMPAIGN_READ', 'AD_CAMPAIGN_CREATE', 'AD_CAMPAIGN_UPDATE', 'AD_CAMPAIGN_ARCHIVE'])) {
      items.push({ label: 'Campañas', icon: Megaphone, route: '/ads/campaigns' });
    }

    return items;
  }, [adsEnabled, hasAnyPermission]);

  const userItems: DropdownItem[] = [
    { label: 'Cambiar Contraseña', icon: Shield, route: '/settings/change-password' },
    { label: 'Cerrar Sesión', icon: LogOut, onClick: handleLogout },
  ];

  return (
    <nav
      className="shadow-lg sticky top-0 z-50"
      style={{ backgroundImage: 'linear-gradient(90deg, var(--brand-primary), var(--brand-secondary), var(--brand-primary))' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <div className="flex items-center space-x-8">
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center space-x-2">
                <img className="h-14" src={navLogo} alt={branding.appName || 'logo'} />
              </Link>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <PermissionGate
                anyPermission={['USER_READ', 'USER_CREATE', 'USER_UPDATE', 'USER_DELETE', 'ISSUES_READ']}
                adminOnly={false}
                hideWhenNoPermission
              >
                {adminItems.length > 0 && (
                  <Dropdown
                    label="Administración"
                    items={adminItems}
                    className="text-white"
                  />
                )}
              </PermissionGate>

              <PermissionGate
                anyPermission={[
                  'CATEGORY_READ', 'CATEGORY_CREATE', 'CATEGORY_UPDATE', 'CATEGORY_DELETE',
                  'TVCHANNEL_READ', 'TVCHANNEL_CREATE', 'TVCHANNEL_UPDATE', 'TVCHANNEL_DELETE',
                  'RADIOSTATION_READ', 'RADIOSTATION_CREATE', 'RADIOSTATION_UPDATE', 'RADIOSTATION_DELETE',
                  'TVGUIDE_READ', 'TVGUIDE_CREATE', 'TVGUIDE_UPDATE', 'TVGUIDE_DELETE',
                  'DOCUMENTARY_READ', 'DOCUMENTARY_CREATE', 'DOCUMENTARY_UPDATE', 'DOCUMENTARY_DELETE',
                  'MOVIE_READ', 'MOVIE_CREATE', 'MOVIE_UPDATE', 'MOVIE_DELETE',
                  'CONCERT_READ', 'CONCERT_CREATE', 'CONCERT_UPDATE', 'CONCERT_DELETE',
                  'TVSHOW_READ', 'TVSHOW_CREATE', 'TVSHOW_UPDATE', 'TVSHOW_DELETE',
                  'SPORT_READ', 'SPORT_CREATE', 'SPORT_UPDATE', 'SPORT_DELETE'
                ]}
                hideWhenNoPermission
              >
                {contentItems.length > 0 && (
                  <Dropdown
                    label="Contenido"
                    items={contentItems}
                    className="text-white"
                  />
                )}
              </PermissionGate>

              <PermissionGate
                anyPermission={['CREDIT_HISTORY_READ', 'MOVIE_READ', 'TVSHOW_READ']}
                hideWhenNoPermission
              >
                {reportItems.length > 0 && (
                  <Dropdown
                    label="Reportes"
                    items={reportItems}
                    className="text-white"
                  />
                )}
              </PermissionGate>

              <PermissionGate
                anyPermission={['AD_CAMPAIGN_READ', 'AD_CAMPAIGN_CREATE', 'AD_CAMPAIGN_UPDATE', 'AD_CAMPAIGN_ARCHIVE']}
                hideWhenNoPermission
              >
                {adsItems.length > 0 && (
                  <Dropdown
                    label="Publicidad"
                    items={adsItems}
                    className="text-white"
                  />
                )}
              </PermissionGate>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <div className="hidden md:flex items-center space-x-4">
              <PermissionGate anyPermission={['APP_SETTINGS_READ', 'APP_SETTINGS_UPDATE', 'HOME_PAGE_SLIDES_MANAGE']} hideWhenNoPermission>
                <Dropdown
                  label="Configuración"
                  items={settingsItems}
                  icon={Settings}
                  className="text-white"
                />
              </PermissionGate>
              <Dropdown
                label={user?.username || 'User'}
                items={userItems}
                icon={User}
                className="text-white"
              />
            </div>
            <ThemeToggle />
            <div className="md:hidden">
              <MobileMenu
                adminItems={adminItems}
                contentItems={contentItems}
                reportItems={reportItems}
                adsItems={adsItems}
                settingsItems={settingsItems}
                userItems={userItems}
              />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

const OverlappingIcons = () => {
  return (
    <div style={{ position: 'relative', width: '24px', height: '24px' }}>
      <Coins size={16} className="mr-3 text-gray-500" style={{ position: 'absolute', top: 3, left: 0 }} />
      <ArrowLeftRight size={12} className="mr-3 text-gray-500" style={{ position: 'absolute', top: 0, left: 8 }} />
    </div>
  );
};

export default NavigationBar;
